import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  FileText, Eye, Download, TrendingUp, Plus, Edit, Trash2,
  Search, Filter, Star, StarOff, Archive, Globe, BarChart3,
  Upload, Image, Calendar, User, Tag, Link as LinkIcon,
  ChevronRight, Settings, BookOpen, ArrowLeft, Save, X,
  Check, AlertCircle, Loader2, ExternalLink, Copy
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import MediaUploadField from "../../components/admin/MediaUploadField";
import MediaLibrary from "../../components/admin/MediaLibrary";
import { OFFICIAL_SEAL } from "@/lib/brand";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  "WAIT Theory",
  "Workforce Intelligence",
  "AI & Workforce",
  "Credential Fatigue",
  "Leadership",
  "Organizational Intelligence",
  "Workforce Economics",
  "Research Reports",
  "Institutional Strategy",
  "Global Workforce Systems"
];

// Helper to generate slug from title
const generateSlug = (title) => {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/[\s_]+/g, '-')
    .replace(/-+/g, '-')
    .trim();
};

export default function SCFRAdminGateway() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("dashboard");
  const [publications, setPublications] = useState([]);
  const [stats, setStats] = useState({
    total_publications: 0,
    total_views: 0,
    total_authors: 0,
    total_categories: 0
  });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  
  // Editor state
  const [showEditor, setShowEditor] = useState(false);
  const [editingPublication, setEditingPublication] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    title: "",
    subtitle: "",
    slug: "",
    author: "Dr. Spencer W. Charles, PhD",
    author_title: "Founder & President",
    institution: "Zodiac Workforce Intelligence Institute",
    category: "WAIT Theory",
    tags: "",
    abstract: "",
    content_html: "",
    featured_image_url: "",
    pdf_url: "",
    is_featured: false,
    is_published: true
  });

  useEffect(() => {
    // Check authentication
    const token = localStorage.getItem('auth_token');
    const role = localStorage.getItem('portal_role');
    if (!token || role !== 'admin') {
      navigate('/internal/admin');
      return;
    }
    
    fetchPublications();
    fetchStats();
  }, [navigate]);

  const fetchPublications = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/scfr/admin/publications`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setPublications(data.publications || []);
    } catch (error) {
      console.error("Error fetching publications:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/scfr/stats`);
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const handleCreateNew = () => {
    setEditingPublication(null);
    setFormData({
      title: "",
      subtitle: "",
      slug: "",
      author: "Dr. Spencer W. Charles, PhD",
      author_title: "Founder & President",
      institution: "Zodiac Workforce Intelligence Institute",
      category: "WAIT Theory",
      tags: "",
      abstract: "",
      content_html: "",
      featured_image_url: "",
      pdf_url: "",
      is_featured: false,
      is_published: false
    });
    setShowEditor(true);
  };

  const handleEdit = (pub) => {
    setEditingPublication(pub);
    setFormData({
      title: pub.title || "",
      subtitle: pub.subtitle || "",
      slug: pub.slug || "",
      author: pub.author || "Dr. Spencer W. Charles, PhD",
      author_title: pub.author_title || "Founder & President",
      institution: pub.institution || "Zodiac Workforce Intelligence Institute",
      category: pub.category || "WAIT Theory",
      tags: (pub.tags || []).join(", "),
      abstract: pub.abstract || "",
      content_html: pub.content_html || "",
      featured_image_url: pub.featured_image_url || "",
      pdf_url: pub.pdf_url || "",
      is_featured: pub.is_featured || false,
      is_published: pub.is_published || false
    });
    setShowEditor(true);
  };

  const handleSave = async () => {
    if (!formData.title || !formData.abstract || !formData.content_html) {
      setSaveMessage({ type: 'error', text: 'Please fill in required fields (Title, Abstract, Content)' });
      return;
    }

    setSaving(true);
    setSaveMessage(null);

    try {
      const token = localStorage.getItem('auth_token');
      const slug = formData.slug || generateSlug(formData.title);
      
      const payload = {
        ...formData,
        slug,
        tags: formData.tags.split(',').map(t => t.trim()).filter(t => t)
      };

      const url = editingPublication 
        ? `${API_URL}/api/scfr/admin/publications/${editingPublication.slug}`
        : `${API_URL}/api/scfr/admin/publications`;
      
      const method = editingPublication ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (response.ok) {
        setSaveMessage({ type: 'success', text: editingPublication ? 'Publication updated!' : 'Publication created!' });
        fetchPublications();
        fetchStats();
        setTimeout(() => {
          setShowEditor(false);
          setSaveMessage(null);
        }, 1500);
      } else {
        setSaveMessage({ type: 'error', text: data.detail || 'Failed to save publication' });
      }
    } catch (error) {
      setSaveMessage({ type: 'error', text: 'Network error. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (slug) => {
    if (!window.confirm('Are you sure you want to delete this publication?')) return;

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/scfr/admin/publications/${slug}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        fetchPublications();
        fetchStats();
      }
    } catch (error) {
      console.error("Error deleting publication:", error);
    }
  };

  const handleToggleFeatured = async (pub) => {
    try {
      const token = localStorage.getItem('auth_token');
      await fetch(`${API_URL}/api/scfr/admin/publications/${pub.slug}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ is_featured: !pub.is_featured })
      });
      fetchPublications();
    } catch (error) {
      console.error("Error toggling featured:", error);
    }
  };

  const handleTogglePublished = async (pub) => {
    try {
      const token = localStorage.getItem('auth_token');
      await fetch(`${API_URL}/api/scfr/admin/publications/${pub.slug}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ is_published: !pub.is_published })
      });
      fetchPublications();
    } catch (error) {
      console.error("Error toggling published:", error);
    }
  };

  const filteredPublications = publications.filter(pub => {
    if (searchTerm && !pub.title.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    if (filterCategory && pub.category !== filterCategory) return false;
    if (filterStatus === 'published' && !pub.is_published) return false;
    if (filterStatus === 'draft' && pub.is_published) return false;
    if (filterStatus === 'featured' && !pub.is_featured) return false;
    return true;
  });

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  // Dashboard View
  const renderDashboard = () => (
    <div className="space-y-8">
      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { icon: FileText, label: "Total Publications", value: stats.total_publications || publications.length, color: "blue" },
          { icon: Eye, label: "Total Views", value: stats.total_views?.toLocaleString() || "0", color: "green" },
          { icon: User, label: "Contributors", value: stats.total_authors || "1", color: "purple" },
          { icon: Tag, label: "Categories", value: stats.total_categories || CATEGORIES.length, color: "amber" }
        ].map((stat, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className={`p-6 bg-${stat.color}-500/10 border border-${stat.color}-500/20 rounded-xl`}
          >
            <stat.icon className={`w-8 h-8 text-${stat.color}-400 mb-3`} />
            <p className="text-3xl font-bold text-white">{stat.value}</p>
            <p className="text-white/50 text-sm">{stat.label}</p>
          </motion.div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="flex flex-wrap gap-4">
        <Button 
          onClick={handleCreateNew}
          className="bg-blue-600 hover:bg-blue-700 text-white gap-2"
        >
          <Plus className="w-4 h-4" />
          New Publication
        </Button>
        <Button 
          variant="outline"
          className="border-white/20 text-white hover:bg-white/10 gap-2"
          onClick={() => setActiveTab("publications")}
        >
          <FileText className="w-4 h-4" />
          Manage Publications
        </Button>
      </div>

      {/* Recent Publications */}
      <div>
        <h3 className="text-xl font-bold text-white mb-4">Recent Publications</h3>
        <div className="space-y-3">
          {publications.slice(0, 5).map((pub) => (
            <div 
              key={pub.id}
              className="flex items-center justify-between p-4 bg-white/[0.02] border border-white/[0.05] rounded-lg hover:bg-white/[0.05] transition-colors"
            >
              <div className="flex items-center gap-4">
                <div className={`w-3 h-3 rounded-full ${pub.is_published ? 'bg-green-500' : 'bg-yellow-500'}`} />
                <div>
                  <h4 className="text-white font-medium">{pub.title}</h4>
                  <p className="text-white/40 text-sm">{pub.category} · {pub.views || 0} views</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {pub.is_featured && <Star className="w-4 h-4 text-amber-400" />}
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-white/50 hover:text-white"
                  onClick={() => handleEdit(pub)}
                >
                  <Edit className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // Publications Manager View
  const renderPublicationsManager = () => (
    <div className="space-y-6">
      {/* Header & Actions */}
      <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
        <div className="flex items-center gap-4 w-full lg:w-auto">
          <div className="relative flex-1 lg:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
            <Input
              type="text"
              placeholder="Search publications..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-white/5 border-white/10 text-white"
            />
          </div>
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white/70 text-sm"
          >
            <option value="">All Categories</option>
            {CATEGORIES.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white/70 text-sm"
          >
            <option value="">All Status</option>
            <option value="published">Published</option>
            <option value="draft">Draft</option>
            <option value="featured">Featured</option>
          </select>
        </div>
        <Button 
          onClick={handleCreateNew}
          className="bg-blue-600 hover:bg-blue-700 text-white gap-2"
        >
          <Plus className="w-4 h-4" />
          New Publication
        </Button>
      </div>

      {/* Publications Table */}
      <div className="bg-white/[0.02] border border-white/[0.05] rounded-xl overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-white/10">
              <th className="text-left text-white/50 text-sm font-medium p-4">Publication</th>
              <th className="text-left text-white/50 text-sm font-medium p-4 hidden md:table-cell">Category</th>
              <th className="text-left text-white/50 text-sm font-medium p-4 hidden lg:table-cell">Views</th>
              <th className="text-left text-white/50 text-sm font-medium p-4">Status</th>
              <th className="text-right text-white/50 text-sm font-medium p-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredPublications.length === 0 ? (
              <tr>
                <td colSpan="5" className="text-center text-white/50 py-12">
                  No publications found. Create your first publication!
                </td>
              </tr>
            ) : (
              filteredPublications.map((pub) => (
                <tr key={pub.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                  <td className="p-4">
                    <div>
                      <h4 className="text-white font-medium line-clamp-1">{pub.title}</h4>
                      <p className="text-white/40 text-sm">{pub.author}</p>
                    </div>
                  </td>
                  <td className="p-4 hidden md:table-cell">
                    <span className="px-2 py-1 bg-blue-500/10 border border-blue-500/20 rounded text-blue-400 text-xs">
                      {pub.category}
                    </span>
                  </td>
                  <td className="p-4 hidden lg:table-cell text-white/60">
                    {pub.views?.toLocaleString() || 0}
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 rounded text-xs ${
                        pub.is_published 
                          ? 'bg-green-500/10 border border-green-500/20 text-green-400'
                          : 'bg-yellow-500/10 border border-yellow-500/20 text-yellow-400'
                      }`}>
                        {pub.is_published ? 'Published' : 'Draft'}
                      </span>
                      {pub.is_featured && (
                        <Star className="w-4 h-4 text-amber-400" />
                      )}
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-white/50 hover:text-white"
                        onClick={() => handleToggleFeatured(pub)}
                        title={pub.is_featured ? "Remove from featured" : "Add to featured"}
                      >
                        {pub.is_featured ? <StarOff className="w-4 h-4" /> : <Star className="w-4 h-4" />}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-white/50 hover:text-white"
                        onClick={() => handleTogglePublished(pub)}
                        title={pub.is_published ? "Unpublish" : "Publish"}
                      >
                        {pub.is_published ? <Archive className="w-4 h-4" /> : <Globe className="w-4 h-4" />}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-white/50 hover:text-white"
                        onClick={() => window.open(`/scfr/${pub.slug}`, '_blank')}
                        title="View"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-white/50 hover:text-white"
                        onClick={() => handleEdit(pub)}
                        title="Edit"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-white/50 hover:text-red-400"
                        onClick={() => handleDelete(pub.slug)}
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  // Publication Editor Modal
  const renderEditor = () => (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm overflow-y-auto">
      <div className="min-h-screen py-8 px-4">
        <div className="max-w-5xl mx-auto bg-slate-900 border border-white/10 rounded-2xl overflow-hidden">
          {/* Editor Header */}
          <div className="flex items-center justify-between p-6 border-b border-white/10">
            <div className="flex items-center gap-3">
              <FileText className="w-6 h-6 text-blue-400" />
              <h2 className="text-xl font-bold text-white">
                {editingPublication ? 'Edit Publication' : 'New Publication'}
              </h2>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="text-white/50 hover:text-white"
              onClick={() => setShowEditor(false)}
            >
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* Editor Content */}
          <div className="p-6 space-y-6 max-h-[calc(100vh-200px)] overflow-y-auto">
            {/* Basic Info */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label className="block text-white/70 text-sm mb-2">Title *</label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value, slug: generateSlug(e.target.value) })}
                  placeholder="Enter publication title"
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-white/70 text-sm mb-2">Subtitle</label>
                <Input
                  value={formData.subtitle}
                  onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                  placeholder="Enter subtitle (optional)"
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
              <div>
                <label className="block text-white/70 text-sm mb-2">URL Slug</label>
                <div className="flex items-center gap-2">
                  <span className="text-white/40 text-sm">/scfr/</span>
                  <Input
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                    placeholder="url-slug"
                    className="bg-white/5 border-white/10 text-white flex-1"
                  />
                </div>
              </div>
              <div>
                <label className="block text-white/70 text-sm mb-2">Category</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white"
                >
                  {CATEGORIES.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Author Info */}
            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <label className="block text-white/70 text-sm mb-2">Author</label>
                <Input
                  value={formData.author}
                  onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
              <div>
                <label className="block text-white/70 text-sm mb-2">Author Title</label>
                <Input
                  value={formData.author_title}
                  onChange={(e) => setFormData({ ...formData, author_title: e.target.value })}
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
              <div>
                <label className="block text-white/70 text-sm mb-2">Institution</label>
                <Input
                  value={formData.institution}
                  onChange={(e) => setFormData({ ...formData, institution: e.target.value })}
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
            </div>

            {/* Tags */}
            <div>
              <label className="block text-white/70 text-sm mb-2">Tags (comma separated)</label>
              <Input
                value={formData.tags}
                onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                placeholder="workforce intelligence, ai, research"
                className="bg-white/5 border-white/10 text-white"
              />
            </div>

            {/* Abstract */}
            <div>
              <label className="block text-white/70 text-sm mb-2">Abstract *</label>
              <Textarea
                value={formData.abstract}
                onChange={(e) => setFormData({ ...formData, abstract: e.target.value })}
                placeholder="Enter the publication abstract..."
                rows={4}
                className="bg-white/5 border-white/10 text-white"
              />
            </div>

            {/* Content HTML */}
            <div>
              <label className="block text-white/70 text-sm mb-2">Content (HTML) *</label>
              <Textarea
                value={formData.content_html}
                onChange={(e) => setFormData({ ...formData, content_html: e.target.value })}
                placeholder="<h2>Introduction</h2><p>Your content here...</p>"
                rows={12}
                className="bg-white/5 border-white/10 text-white font-mono text-sm"
              />
              <p className="text-white/40 text-xs mt-2">
                Supports HTML: &lt;h2&gt;, &lt;h3&gt;, &lt;p&gt;, &lt;ul&gt;, &lt;ol&gt;, &lt;li&gt;, &lt;strong&gt;, &lt;em&gt;, &lt;blockquote&gt;
              </p>
            </div>

            {/* Media Uploads */}
            <div className="grid md:grid-cols-2 gap-6">
              <MediaUploadField
                kind="image"
                label="Featured Image"
                testIdPrefix="editor-image"
                value={formData.featured_image_url}
                onChange={(media) => {
                  const url =
                    media && typeof media === "object"
                      ? `${API_URL}${media.public_url}`
                      : media || "";
                  setFormData({ ...formData, featured_image_url: url });
                }}
                helpText="Cinematic cover for the publication. JPG/PNG/WebP, max 10 MB."
              />
              <MediaUploadField
                kind="pdf"
                label="Publication PDF"
                testIdPrefix="editor-pdf"
                value={formData.pdf_url}
                onChange={(media) => {
                  const url =
                    media && typeof media === "object"
                      ? `${API_URL}${media.public_url}`
                      : media || "";
                  setFormData({ ...formData, pdf_url: url });
                }}
                helpText="Optional downloadable PDF. Max 25 MB."
              />
            </div>

            {/* Publish Settings */}
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_published}
                  onChange={(e) => setFormData({ ...formData, is_published: e.target.checked })}
                  className="w-4 h-4 rounded border-white/20 bg-white/5"
                />
                <span className="text-white/70">Publish immediately</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_featured}
                  onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
                  className="w-4 h-4 rounded border-white/20 bg-white/5"
                />
                <span className="text-white/70">Featured publication</span>
              </label>
            </div>

            {/* Save Message */}
            {saveMessage && (
              <div className={`flex items-center gap-2 p-4 rounded-lg ${
                saveMessage.type === 'success' 
                  ? 'bg-green-500/10 border border-green-500/20 text-green-400'
                  : 'bg-red-500/10 border border-red-500/20 text-red-400'
              }`}>
                {saveMessage.type === 'success' ? <Check className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
                {saveMessage.text}
              </div>
            )}
          </div>

          {/* Editor Footer */}
          <div className="flex items-center justify-between p-6 border-t border-white/10 bg-white/[0.02]">
            <Button
              variant="ghost"
              className="text-white/50 hover:text-white"
              onClick={() => setShowEditor(false)}
            >
              Cancel
            </Button>
            <div className="flex items-center gap-3">
              {formData.slug && (
                <Button
                  variant="outline"
                  className="border-white/20 text-white/70 hover:bg-white/10 gap-2"
                  onClick={() => copyToClipboard(`${window.location.origin}/scfr/${formData.slug}`)}
                >
                  <Copy className="w-4 h-4" />
                  Copy URL
                </Button>
              )}
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-blue-600 hover:bg-blue-700 text-white gap-2"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    {editingPublication ? 'Update' : 'Create'} Publication
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-950" data-testid="scfr-admin-gateway">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-slate-950/95 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/internal/admin/dashboard')}
                className="flex items-center gap-2 text-white/60 hover:text-white transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="hidden sm:inline">Back to Admin</span>
              </button>
              <div className="h-6 w-px bg-white/20" />
              <div className="flex items-center gap-3">
                <img
                  src={OFFICIAL_SEAL}
                  alt="Zodiac Institute"
                  className="w-9 h-9 object-contain drop-shadow-[0_2px_6px_rgba(200,161,87,0.35)]"
                  data-testid="scfr-admin-seal"
                />
                <div className="leading-tight">
                  <div className="text-white font-semibold text-sm">SCFR-Journal Admin</div>
                  <div className="text-[10px] uppercase tracking-widest text-amber-300/80">Zodiac Institute</div>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                className="text-white/50 hover:text-white"
                onClick={() => window.open('/scfr-journal', '_blank')}
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                View Journal
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <div className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-6">
            {[
              { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
              { id: 'publications', label: 'Publications', icon: FileText },
              { id: 'media', label: 'Media Library', icon: Image },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 py-4 border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-white'
                    : 'border-transparent text-white/50 hover:text-white'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
          </div>
        ) : (
          <>
            {activeTab === 'dashboard' && renderDashboard()}
            {activeTab === 'publications' && renderPublicationsManager()}
            {activeTab === 'media' && <MediaLibrary />}
          </>
        )}
      </main>

      {/* Editor Modal */}
      {showEditor && renderEditor()}
    </div>
  );
}
